<script>

import Annotation from "@/classes/annotation";
import { TEXT_LINE_LABEL, isBlackoutLabelName } from '@/utils/textLabels';
const { v4: uuidV4 } = require('uuid');
import createSocket from '@/utils/socket';
import {
  modelHasBrowserTflite,
  runAnnotateLiteRtInference,
} from '@/utils/annotateLiteRtInference';


export default {
  components: {
    //
  },

  props: [
    //
  ],

  mixins: [
      //
  ],

  data: function () {
    return {
      annotation_suggestions: [],
      suggestion_match_logs: [],
      annotations: [],
      initial_annotations: false,
      model_annotations: false,
      mutations: [],
      meta: '',
      'ctrl_pressed': false,
      last_annotation_added: false,
      run_model_timeout: null,
      run_model_loading: false,
      /** Set when annotate modal used LiteRT.js instead of a run_model job. */
      litert_inference_status: '',
      model_predictions_socket: null,
      model_predictions_socket_token: null,
      auto_boxes_enabled: false,
      auto_boxes_query: '',
      auto_boxes_loading: false,
      auto_boxes_socket: null,
      auto_boxes_socket_token: null,
      auto_boxes_status_updates: [],
      text_boxes_loading: false,
      text_boxes_socket: null,
      text_boxes_socket_token: null,
      text_boxes_status_updates: [],
      text_suggestions_loading: false,
      show_suggest_text_tools: false,
      asset_messages: [],
      pendingPlacement: null,
    };
  },

  computed: {
    /** LLM auto-box and detect-text UI on annotate page/modal (re-enable when ready). */
    showLlmAnnotateAiTools() {
      return false;
    },
    'annotation_selected' () {
      let selected = this.annotations.filter(a=>{return a.selected === true});
      if(selected.length>0) return selected[0];
      return false;
    },
    selected_annotations() {
      return this.annotations.filter((a)=>{return a.selected;})
    },
    hasEncodedBoundingBoxes() {
      const meta = this.parseAnnotateMeta();
      if (!meta?.medication_detection_data) {
        return false;
      }
      try {
        const medicationDetectionData = JSON.parse(meta.medication_detection_data);
        const encodedBoundingBoxes = medicationDetectionData?.encodedBoundingBoxes;
        return Array.isArray(encodedBoundingBoxes) && encodedBoundingBoxes.length > 0;
      } catch {
        return false;
      }
    },
    /** Modal uses asset_id_cached once opened; full page uses asset_id. */
    annotate_asset_id() {
      if (Object.prototype.hasOwnProperty.call(this.$data, 'asset_id_cached')) {
        const c = this.asset_id_cached;
        if (c !== null && c !== undefined && c !== '') {
          return c;
        }
      }
      return this.asset_id;
    },
    text_suggestions() {
      const actions = new Set(['add_text_box', 'suggest_transcript']);
      return (this.annotation_suggestions || []).filter((s) => {
        const p = this.parseAnnotationSuggestion(s);
        return actions.has(p.action);
      });
    },
    selected_annotation_id() {
      const sel = this.annotations.filter((a) => a.selected);
      return sel.length > 0 ? sel[0].id : null;
    },
    labels() {
      return this.$store.getters['label/get_items'];
    },
  },

  created() {
    //
  },

  mounted() {
    window.addEventListener('keydown', this.addEventsKeyDown);
    window.addEventListener('keyup', this.addEventsKeyUp);
  },

  beforeUnmount() {
    if (this.run_model_timeout) {
      clearTimeout(this.run_model_timeout);
    }
    this.teardownAutoBoxesSocket();
    this.teardownTextBoxesSocket();
    this.teardownModelPredictionsSocket();
    window.removeEventListener('keydown', this.addEventsKeyDown);
    window.removeEventListener('keyup', this.addEventsKeyUp);
  },

  methods: {
    parseAnnotateMeta() {
      const raw = this.meta;
      if (!raw) {
        return null;
      }
      try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return null;
      }
    },
    _textFieldsFromServer(annotation) {
      return {
        transcript: annotation.transcript || '',
        transcript_source: annotation.transcript_source || '',
      };
    },
    _isAxisAlignedQuadCoords(coords) {
      if (!Array.isArray(coords) || coords.length !== 8) {
        return false;
      }
      const xs = [coords[0], coords[2], coords[4], coords[6]];
      const ys = [coords[1], coords[3], coords[5], coords[7]];
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const eps = 1e-5;
      for (let i = 0; i < 4; i += 1) {
        const onVertical = Math.abs(xs[i] - minX) < eps || Math.abs(xs[i] - maxX) < eps;
        const onHorizontal = Math.abs(ys[i] - minY) < eps || Math.abs(ys[i] - maxY) < eps;
        if (!onVertical || !onHorizontal) {
          return false;
        }
      }
      return (maxX - minX) > eps && (maxY - minY) > eps;
    },
    _quadCoordsToXYWH(coords) {
      const xs = [coords[0], coords[2], coords[4], coords[6]];
      const ys = [coords[1], coords[3], coords[5], coords[7]];
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      return [minX, minY, Math.max(...xs) - minX, Math.max(...ys) - minY];
    },
    _annotationFromServer(annotation, color) {
      const id = annotation.id;
      let type = annotation.type === 2 ? 'point' : 'box';
      if (Array.isArray(annotation.coords) && annotation.coords.length === 2) {
        type = 'point';
      } else if (
        annotation.format === 'normalized_quad'
        || (Array.isArray(annotation.coords) && annotation.coords.length === 8)
      ) {
        type = this._isAxisAlignedQuadCoords(annotation.coords) ? 'box' : 'polygon';
      }
      const colorValues = isBlackoutLabelName(annotation.label) ? [0, 0, 0] : color;
      let coords = annotation.coords;
      if (type === 'box' && Array.isArray(coords) && coords.length === 8) {
        coords = this._quadCoordsToXYWH(coords);
      }
      return new Annotation(
        id,
        type,
        coords,
        annotation.conf,
        annotation.label,
        colorValues,
        true,
        this._textFieldsFromServer(annotation),
        annotation.format || '',
      );
    },
    addEventsKeyUp: function(e) {
      let this2 = this;
      if(e.keyCode === 17) { // ctrl
          this2.ctrl_pressed = false;
        }
    },
    addEventsKeyDown: function(e) {
        let this2 = this;

        let activeElement = document.activeElement;
        let inputs = ['input', 'select', 'button', 'textarea'];

        if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1) {
            return false;
        }

        if(e.keyCode === 17) { // ctrl
          this2.ctrl_pressed = true;
        }
        if(this.ctrl_pressed && e.keyCode === 67) { // ctrl + c
          this.selected_annotations.forEach((selected_annotation) => {
            this2.copyAnnotation(selected_annotation, 0.01, 0.01)
          })
        }
        if(this.ctrl_pressed && e.keyCode === 86) { // ctrl + v
          this.selected_annotations.forEach((selected_annotation) => {
            this2.copyAnnotation(selected_annotation)
          })
        }
        if(e.keyCode === 8) { // delete
          let annotations = this2.annotations.filter(annotation=>{return !annotation.selected})
          this2.annotations = annotations;
        }
        if(e.keyCode === 39) { // arrow right
          this.selected_annotations.forEach((selected_annotation) => {
            let index = this.annotations.map((a)=>{return a.id;}).indexOf(selected_annotation.id)
            this.annotations[index].coords[0] = this.annotations[index].coords[0] + 0.005
            this2.annotations[index] = this.annotations[index];
          });
        }
          if(e.keyCode === 37) { // arrow left
            this.selected_annotations.forEach((selected_annotation) => {
              let index = this.annotations.map((a)=>{return a.id;}).indexOf(selected_annotation.id)
              this.annotations[index].coords[0] = this.annotations[index].coords[0] - 0.005
              this2.annotations[index] = this.annotations[index];
            });
          }
          if(e.keyCode === 38) { // arrow up
            this.selected_annotations.forEach((selected_annotation) => {
              let index = this.annotations.map((a)=>{return a.id;}).indexOf(selected_annotation.id)
              this.annotations[index].coords[1] = this.annotations[index].coords[1] - 0.005
              this2.annotations[index] = this.annotations[index];
            });
          }
          if(e.keyCode === 40) { // arrow down
            this.selected_annotations.forEach((selected_annotation) => {
              let index = this.annotations.map((a)=>{return a.id;}).indexOf(selected_annotation.id)
              this.annotations[index].coords[1] = this.annotations[index].coords[1] + 0.005
              this2.annotations[index] = this.annotations[index];
            });
          }
      },
    copyAnnotation(annotation, move_x=0.0, move_y=0.0) {
      let clone = JSON.parse(JSON.stringify(annotation))
      clone.selected = true;
      clone.coords[0] = clone.coords[0] + move_x
      clone.coords[1] = clone.coords[1] + move_y
      clone.id = this.getUniqueId();
      clone.conf = '1'
      clone.skip_zoom = true;
      const annotations = this.annotations.map((a) => {
        if (a.id === annotation.id) {
          a.selected = false;
        }
        return a;
      });
      annotations.push(clone);
      this.annotations = annotations;
    },
    async loadModalAnnotations(model_id, asset_id) {

      if(this.model_annotations) {
        this.annotations = this.model_annotations
      }
      else {
        this.run_model_loading = true
        this.litert_inference_status = ''

        const model = (this.models || []).find((m) => m && m.id === model_id);
        if (model && modelHasBrowserTflite(model) && this.asset_url) {
          try {
            const projectId = sessionStorage.getItem('project_id');
            const result = await runAnnotateLiteRtInference({
              model,
              assetImageUrl: this.asset_url,
              projectId,
              token: localStorage.token,
            });
            let i = 0;
            const annotations = (result.annotations || []).map((annotation) => {
              const ann = { ...annotation };
              if (!ann.id) {
                ann.id = this.getUniqueId();
              }
              const color = (result.colors && result.colors[i]) || [249, 65, 68];
              i += 1;
              return this._annotationFromServer(ann, color);
            });
            this.annotations = annotations;
            this.model_annotations = annotations;
            this.litert_inference_status = this.$t
              ? this.$t('annotate.litert_ran_in_browser')
              : 'Ran in browser (LiteRT)';
            this.run_model_loading = false;
            this.loading = false;
            return;
          } catch (e) {
            // Fall through to server runner if LiteRT fails.
            console.warn('LiteRT annotate inference failed, falling back to runner', e);
            this.litert_inference_status = '';
          }
        }

        // Generate unique socket token for this model run
        const socketToken = uuidV4();
        this.model_predictions_socket_token = socketToken;
        this.setupModelPredictionsSocket(socketToken);

        let result = await this.$api.model.get_model_preds(JSON.parse(JSON.stringify(asset_id)), model_id, socketToken);

        if (result.annotations) {
          // Predictions already exist
          let this2 = this;
          let annotations = result.annotations
          let i = 0;
          annotations = annotations.map(annotation=>{
            const ann = { ...annotation };
            if (!ann.id) {
              ann.id = this2.getUniqueId();
            }
            const color = result.colors[i];
            i += 1;
            return this2._annotationFromServer(ann, color);
          });
          this2.annotations = annotations
          this2.model_annotations = annotations
          this.run_model_loading = false
          this.teardownModelPredictionsSocket();
        }
        // If no annotations, we'll receive them via websocket
      }
      this.loading = false
    },
    setupModelPredictionsSocket(socketToken) {
      this.teardownModelPredictionsSocket();
      this.model_predictions_socket_token = socketToken;
      const socket = createSocket();
      this.model_predictions_socket = socket;
      socket.on('connect', () => {
        socket.emit('subscribe', socketToken);
      });
      socket.on('message', (message) => {
        let payload = null;
        try {
          payload = JSON.parse(JSON.parse(message));
        } catch (e) {
          return;
        }
        if (!payload || payload.type !== 'model_predictions') {
          return;
        }
        if (payload.status === 'ready') {
          // Predictions are ready, reload them
          this.loadModalAnnotations(this.last_model_id, this.last_asset_id);
        }
      });
    },
    teardownModelPredictionsSocket() {
      if (this.model_predictions_socket) {
        this.model_predictions_socket.close();
      }
      this.model_predictions_socket = null;
      this.model_predictions_socket_token = null;
    },
    refresh_annotations() {
      this.annotations = this.annotations.slice();
    },
    async load() {
      const assetId = this.annotate_asset_id;
      if (!assetId) {
        this.loading = false;
        return;
      }
      this.loading = true
      let result = await this.$api.asset.load(assetId);
      //result eventually contains the annotation_suggestions.

      let label_meta = result.label_meta;
      let annotations = result.annotations
      let i = 0;
      annotations = annotations.map(annotation=>{
        let color = result.colors[i];
        i += 1;
        if (label_meta[annotation.label] && label_meta[annotation.label].color) {
          color = label_meta[annotation.label].color.split(',');
        }
        return this._annotationFromServer(annotation, color);
      });

      // Hier gaan we iets leuks doen met de annotation suggestions

      this.annotation_suggestions = result.annotation_suggestions
      this.suggestion_match_logs = result.suggestion_match_logs || []
      this.asset_messages = result.asset_messages || []
      this.annotations = []
      this.asset_url = this.$api_host + result.path
      this.annotations = annotations
      this.loading = false
      this.meta = JSON.parse(result.meta)
      this.mutations = Array.isArray(result.mutations) ? result.mutations : [];
      this.initial_annotations = JSON.parse(JSON.stringify(this.annotations));
    },
    async archiveAssetMessage(messageId) {
      const aid = this.annotate_asset_id;
      if (!aid || messageId == null) {
        return;
      }
      try {
        const res = await this.$api.asset.archiveAssetMessage(aid, messageId);
        if (res == null) {
          return;
        }
        if (res.error) {
          this.$store.dispatch(
            'snackbars/add_failed_snack',
            String(res.error || this.$t('annotate.asset_messages_archive_failed')),
          );
          return;
        }
        // Update messages in place — do not reload the asset. A full load()
        // would wipe unsaved local annotation edits (and editor mutations).
        this.asset_messages = (this.asset_messages || []).filter(
          (m) => String(m.id) !== String(messageId),
        );
      } catch (e) {
        this.$store.dispatch(
          'snackbars/add_failed_snack',
          this.$t('annotate.asset_messages_archive_failed'),
        );
      }
    },
    onAssetMessageAdded(message) {
      if (!message || message.id == null) {
        return;
      }
      const next = [...(this.asset_messages || [])];
      if (next.some((m) => String(m.id) === String(message.id))) {
        return;
      }
      next.push(message);
      this.asset_messages = next;
    },
    getUniqueId() {
      return Math.random().toString(36).slice(2);
    },
    deleteAnnotation(id) {
      this.annotations = this.annotations.filter(annotation => { return annotation.id !== id; });
    },
    resetAnnotations() {
      this.model_results_shown = false
      this.litert_inference_status = ''
      this.annotations = this.initial_annotations;
    },
    clearAnnotations(less_than) {
      if(less_than) {
        let annotations = this.annotations.filter(annotation => { return annotation.conf > less_than; });
        this.annotations = annotations;
      }
      else {
        this.annotations = [];
      }
    },
    add_annotation_deselect_others(annotation) {
      let annotations = this.annotations.map(a => {
        if(a.id !== annotation.id) {
          a.selected = false;
        }
        return a;
      })

      annotations.push(annotation);
      this.annotations = annotations
    },
    addAnnotation(annotation) {
      this.last_annotation_added = annotation
      this.annotations.push(annotation);
    },
    addAnnotationClearSelection(annotation) {
      const annotations = this.annotations.map((a) => {
        a.selected = false;
        return a;
      });
      annotation.selected = true;
      annotations.push(annotation);
      this.annotations = annotations;
    },
    addSuggestionAnnotation(annotation) {
      this.addAnnotationClearSelection(annotation);
    },
    onLabelSelectedForPlacement({ type, name, color_values }) {
      const resolvedColor = isBlackoutLabelName(name) ? [0, 0, 0] : color_values;
      this.pendingPlacement = { type, name, color_values: resolvedColor };
    },
    clearPendingPlacement() {
      this.pendingPlacement = null;
    },
    onPlaceOnCanvas({ type, coords }) {
      if (!this.pendingPlacement || this.pendingPlacement.type !== type) {
        return;
      }
      const placement = this.pendingPlacement;
      const annotation = new Annotation(
        uuidV4(),
        type,
        coords,
        1.0,
        placement.name,
        placement.color_values,
        true,
      );
      if (typeof this.addSuggestionAnnotation === 'function') {
        this.addSuggestionAnnotation(annotation);
      } else {
        this.addAnnotationClearSelection(annotation);
      }
      this.clearPendingPlacement();
    },
    async ensureLabelsCatalog() {
      if (!this.$store) {
        return;
      }
      const projectId =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('project_id')
          : null;
      if (!projectId) {
        return;
      }
      const loaded = this.$store.getters['label/get_loaded'] === true;
      const loadedPid = this.$store.getters['label/get_loaded_for_project_id'];
      if (loaded && String(loadedPid) === String(projectId)) {
        return;
      }
      await this.$store.dispatch('label/prefetch_all_background');
    },
    labelNameForId(labelId) {
      const label = this.labels.find((l) => l.id == labelId);
      return label ? label.name : String(labelId);
    },
    suggestionLabelName(suggestion) {
      if (!suggestion) {
        return '';
      }
      if (suggestion.label_name != null && String(suggestion.label_name).trim() !== '') {
        return String(suggestion.label_name);
      }
      if (suggestion.label != null && String(suggestion.label).trim() !== '') {
        const asString = String(suggestion.label);
        // Prefer real names over bare numeric ids when label_id lookup can resolve them.
        if (Number.isNaN(Number(asString)) || asString !== String(suggestion.label_id)) {
          return asString;
        }
      }
      return this.labelNameForId(suggestion.label_id);
    },
    parseSuggestionField(value) {
      if (value === null || value === undefined) {
        return value;
      }
      if (typeof value === 'object') {
        return value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return value;
        }
        try {
          return JSON.parse(trimmed);
        } catch (e) {
          return value;
        }
      }
      return value;
    },
    parseAnnotationSuggestion(record) {
      if (!record) {
        return {};
      }
      const raw = record.suggestion;
      if (raw && typeof raw === 'object') {
        return raw;
      }
      return this.parseSuggestionField(raw) || {};
    },
    suggestionCoords(suggestion) {
      const coords = this.parseSuggestionField(suggestion.coords);
      if (Array.isArray(coords)) {
        return coords;
      }
      if (coords && typeof coords === 'object' && Array.isArray(coords.coords)) {
        return coords.coords;
      }
      return coords;
    },
    suggestionConfidence(suggestion) {
      return this.parseSuggestionField(suggestion.confidence);
    },
    async show_suggestions() {
      await this.ensureLabelsCatalog();
      this.annotation_suggestions.forEach((annotation_suggestion) => {
        let suggestion = this.parseAnnotationSuggestion(annotation_suggestion);
        if (suggestion.action === 'add_bounding_box') {
          let suggested_label_name = this.suggestionLabelName(suggestion);
          let id = uuidV4();
          let color = [0, 180, 0];
          let annotation = new Annotation(
            id,
            'box',
            this.suggestionCoords(suggestion),
            this.suggestionConfidence(suggestion),
            suggested_label_name,
            color,
          );
          this.addSuggestionAnnotation(annotation);
        } else if (suggestion.action === 'add_keypoint') {
          let suggested_label_name = this.suggestionLabelName(suggestion);
          let id = uuidV4();
          let color = [0, 180, 0];
          let annotation = new Annotation(
            id,
            'point',
            this.suggestionCoords(suggestion),
            this.suggestionConfidence(suggestion),
            suggested_label_name,
            color,
          );
          this.addSuggestionAnnotation(annotation);
        } else if (suggestion.action === 'delete_annotation') {
          this.annotations = this.annotations.filter(
            (annotation) => annotation.id !== annotation_suggestion.annotation_id,
          );
        } else if (suggestion.action === 'rename_label_annotation') {
          let suggested_label_name = this.suggestionLabelName(suggestion);
          const targetId = annotation_suggestion.annotation_id;
          this.annotations = this.annotations.map((annotation) => {
            if (annotation.id === targetId) {
              annotation.label = suggested_label_name;
            }
            return annotation;
          });
        }
      });
      this.show_annotation_suggestions = false;
    },
    hide_suggestions() {
      this.annotations = JSON.parse(JSON.stringify(this.initial_annotations));
      this.show_annotation_suggestions = true;
    },
    selectAnnotation(selectedAnnotationId, shift_pressed, skip_reload) {
        let annotations = this.annotations.map(annotation=>{
            if(selectedAnnotationId === annotation.id) {
              annotation.selected = true;
            }
            else if(!shift_pressed) {
              annotation.selected = false
            }
            return annotation;
          });
        if(!skip_reload) {
          this.annotations = annotations
        }
    },
    unselectAnnotation(id) {
      this.annotations = this.annotations.map(annotation => {
        if (annotation.id === id) {
          annotation.selected = false;
        }
        return annotation;
      });
    },
    updateAnnotation(id, coords) {
      this.annotations = this.annotations.map((annotation) => {
        if (annotation.id === id) {
          annotation.coords = coords;
        }
        return annotation;
      });
    },
    updateAnnotationLabel(id, label) {
      this.annotations = this.annotations.map(annotation => {
        if(annotation.id === id) {
          annotation.label = label;
          annotation.conf = '1'
        }
        return annotation;
      });
    },
    async runAutoBoxes() {
      const q = (this.auto_boxes_query || '').trim();
      if (!q) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.auto_boxes_no_query'));
        return;
      }
      const aid = this.annotate_asset_id;
      if (!aid) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.auto_boxes_error'));
        return;
      }
      this.auto_boxes_loading = true;
      this.auto_boxes_status_updates = [];
      this.addAutoBoxesStatus('queued');
      const token = uuidV4();
      this.auto_boxes_socket_token = token;
      this.setupAutoBoxesSocket(token, q);
      const res = await this.$api.annotation.auto_boxes(aid, q, token);
      if (!res) {
        this.auto_boxes_loading = false;
        this.teardownAutoBoxesSocket();
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.auto_boxes_error'));
        return;
      }
      if (res.error) {
        this.auto_boxes_loading = false;
        this.teardownAutoBoxesSocket();
        this.addAutoBoxesStatus('error: ' + String(res.error));
        this.$store.dispatch('snackbars/add_failed_snack', String(res.error));
      }
    },
    addAutoBoxesStatus(text) {
      const line = String(text);
      const next = (this.auto_boxes_status_updates || []).slice(-7);
      next.push(line);
      this.auto_boxes_status_updates = next;
      console.log('[auto-boxes]', line);
    },
    teardownAutoBoxesSocket() {
      if (this.auto_boxes_socket) {
        this.auto_boxes_socket.close();
      }
      this.auto_boxes_socket = null;
      this.auto_boxes_socket_token = null;
    },
    setupAutoBoxesSocket(token, query) {
      this.teardownAutoBoxesSocket();
      const socket = createSocket();
      this.auto_boxes_socket = socket;
      socket.on('connect', () => {
        socket.emit('subscribe', token);
      });
      socket.on('message', (message) => {
        let payload = null;
        try {
          payload = JSON.parse(JSON.parse(message));
        } catch (e) {
          return;
        }
        if (!payload || payload.type !== 'llm_auto_boxes') {
          return;
        }
        if (payload.status === 'processing' && payload.message) {
          this.addAutoBoxesStatus(payload.message);
          this.$store.dispatch('snackbars/add_success_snack', String(payload.message));
          return;
        }
        if (payload.status === 'error') {
          this.auto_boxes_loading = false;
          this.addAutoBoxesStatus('error: ' + String(payload.error || this.$t('annotate.auto_boxes_error')));
          this.$store.dispatch('snackbars/add_failed_snack', String(payload.error || this.$t('annotate.auto_boxes_error')));
          this.teardownAutoBoxesSocket();
          return;
        }
        if (payload.status === 'completed') {
          this.auto_boxes_loading = false;
          this.addAutoBoxesStatus('completed');
          this.applyAutoBoxesResult(payload.boxes, query);
          this.teardownAutoBoxesSocket();
        }
      });
    },
    applyAutoBoxesResult(boxes, query) {
      if (!Array.isArray(boxes) || boxes.length === 0) {
        this.addAutoBoxesStatus('result: 0 boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.auto_boxes_error'));
        return;
      }
      const labelDefault = query;
      const color = [255, 140, 0];
      let added = 0;
      for (let i = 0; i < boxes.length; i += 1) {
        const b = boxes[i];
        if (!b || !Array.isArray(b.coords) || b.coords.length !== 4) {
          continue;
        }
        const id = this.getUniqueId();
        const label = b.label != null && String(b.label).trim() !== '' ? String(b.label) : labelDefault;
        const ann = new Annotation(id, 'box', b.coords, '0.9', label, color, true);
        this.addAnnotationClearSelection(ann);
        added += 1;
      }
      if (added === 0) {
        this.addAutoBoxesStatus('result: 0 valid boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.auto_boxes_error'));
        return;
      }
      this.addAutoBoxesStatus('result: ' + String(added) + ' box(es)');
      this.$store.dispatch('snackbars/add_success_snack', String(added) + ' box(es) added');
    },
    async runSuggestTextBoxes() {
      const aid = this.annotate_asset_id;
      if (!aid) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.detect_text_boxes_error'));
        return;
      }
      this.text_boxes_loading = true;
      this.text_boxes_status_updates = [];
      this.addTextBoxesStatus('queued');
      const token = uuidV4();
      this.text_boxes_socket_token = token;
      this.setupTextBoxesSocket(token);
      const res = await this.$api.annotation.detect_text_boxes(aid, token);
      if (!res) {
        this.text_boxes_loading = false;
        this.teardownTextBoxesSocket();
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.detect_text_boxes_error'));
        return;
      }
      if (res.error) {
        this.text_boxes_loading = false;
        this.teardownTextBoxesSocket();
        this.addTextBoxesStatus('error: ' + String(res.error));
        this.$store.dispatch('snackbars/add_failed_snack', String(res.error));
      }
    },
    async runSuggestTextRecognition() {
      const aid = this.annotate_asset_id;
      if (!aid) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.suggest_text_error'));
        return;
      }
      let annotationIds = null;
      const selected = this.annotations.filter((a) => a.selected && a.type === 'box');
      if (selected.length > 0) {
        annotationIds = selected
          .map((a) => a.id)
          .filter((id) => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id)));
      }
      this.text_boxes_loading = true;
      this.text_boxes_status_updates = [];
      this.addTextBoxesStatus('queued (text)');
      const token = uuidV4();
      this.text_boxes_socket_token = token;
      this.setupTextBoxesSocket(token);
      const res = await this.$api.annotation.suggest_text_recognition(aid, token, annotationIds);
      if (!res) {
        this.text_boxes_loading = false;
        this.teardownTextBoxesSocket();
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.suggest_text_error'));
        return;
      }
      if (res.error) {
        this.text_boxes_loading = false;
        this.teardownTextBoxesSocket();
        this.addTextBoxesStatus('error: ' + String(res.error));
        this.$store.dispatch('snackbars/add_failed_snack', String(res.error));
      }
    },
    addTextBoxesStatus(text) {
      const line = String(text);
      const next = (this.text_boxes_status_updates || []).slice(-7);
      next.push(line);
      this.text_boxes_status_updates = next;
      console.log('[detect-text-boxes]', line);
    },
    teardownTextBoxesSocket() {
      if (this.text_boxes_socket) {
        this.text_boxes_socket.close();
      }
      this.text_boxes_socket = null;
      this.text_boxes_socket_token = null;
    },
    setupTextBoxesSocket(token) {
      this.teardownTextBoxesSocket();
      const socket = createSocket();
      this.text_boxes_socket = socket;
      socket.on('connect', () => {
        socket.emit('subscribe', token);
      });
      socket.on('message', (message) => {
        let payload = null;
        try {
          payload = JSON.parse(JSON.parse(message));
        } catch (e) {
          return;
        }
        if (!payload || payload.type !== 'paddle_text_boxes') {
          return;
        }
        if (payload.status === 'processing' && payload.message) {
          this.addTextBoxesStatus(payload.message);
          this.$store.dispatch('snackbars/add_success_snack', String(payload.message));
          return;
        }
        if (payload.status === 'error') {
          this.text_boxes_loading = false;
          this.addTextBoxesStatus('error: ' + String(payload.error || this.$t('annotate.detect_text_boxes_error')));
          this.$store.dispatch('snackbars/add_failed_snack', String(payload.error || this.$t('annotate.detect_text_boxes_error')));
          this.teardownTextBoxesSocket();
          return;
        }
        if (payload.status === 'completed') {
          this.text_boxes_loading = false;
          this.addTextBoxesStatus('completed');
          if (payload.use_suggestions) {
            this.refreshTextSuggestions().then(() => {
              const n = payload.suggestion_count || this.text_suggestions.length;
              this.addTextBoxesStatus('result: ' + String(n) + ' suggestion(s)');
              this.$store.dispatch('snackbars/add_success_snack', String(n) + ' OCR suggestion(s) ready');
            });
          } else {
            this.applyTextBoxesResult(payload.boxes);
          }
          this.teardownTextBoxesSocket();
        }
      });
    },
    async refreshTextSuggestions() {
      const aid = this.annotate_asset_id;
      if (!aid) {
        return;
      }
      const res = await this.$api.annotation.list_text_suggestions(aid);
      if (res && Array.isArray(res)) {
        this.annotation_suggestions = res;
      } else if (this.annotate_asset_id) {
        await this.load();
      }
    },
    async acceptTextSuggestions(suggestionIds) {
      const aid = this.annotate_asset_id;
      if (!aid || !suggestionIds || suggestionIds.length === 0) {
        return;
      }
      this.text_suggestions_loading = true;
      const res = await this.$api.annotation.accept_text_suggestions(aid, suggestionIds);
      this.text_suggestions_loading = false;
      if (!res || res.error) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.text_suggestions_error'));
        return;
      }
      await this.load();
      this.$store.dispatch('snackbars/add_success_snack', String(res.applied || 0) + ' accepted');
    },
    async rejectTextSuggestions(suggestionIds) {
      const aid = this.annotate_asset_id;
      if (!aid || !suggestionIds || suggestionIds.length === 0) {
        return;
      }
      this.text_suggestions_loading = true;
      const res = await this.$api.annotation.reject_text_suggestions(aid, suggestionIds);
      this.text_suggestions_loading = false;
      if (!res || res.error) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.text_suggestions_error'));
        return;
      }
      await this.refreshTextSuggestions();
    },
    acceptAllTextSuggestions() {
      const ids = this.text_suggestions.map((s) => s.id);
      return this.acceptTextSuggestions(ids);
    },
    rejectAllTextSuggestions() {
      const ids = this.text_suggestions.map((s) => s.id);
      return this.rejectTextSuggestions(ids);
    },
    updateAnnotationTranscript({ id, transcript, transcript_source }) {
      this.annotations = this.annotations.map((annotation) => {
        if (annotation.id === id) {
          annotation.transcript = transcript != null ? String(transcript) : '';
          annotation.transcript_source = transcript_source || 'manual';
        }
        return annotation;
      });
    },
    applyTextBoxesResult(boxes) {
      if (!Array.isArray(boxes) || boxes.length === 0) {
        this.addTextBoxesStatus('result: 0 boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.detect_text_boxes_error'));
        return;
      }
      const color = [100, 200, 255];
      let added = 0;
      for (let i = 0; i < boxes.length; i += 1) {
        const b = boxes[i];
        if (!b || !Array.isArray(b.coords) || (b.coords.length !== 4 && b.coords.length !== 8)) {
          continue;
        }
        const id = this.getUniqueId();
        const label = TEXT_LINE_LABEL;
        const transcript = b.label != null && String(b.label).trim() !== '' && String(b.label).trim().toLowerCase() !== 'text'
          ? String(b.label).trim()
          : (b.transcript || '');
        let annCoords = b.coords;
        let annType = annCoords.length === 8 ? 'polygon' : 'box';
        if (annType === 'polygon' && this._isAxisAlignedQuadCoords(annCoords)) {
          annType = 'box';
          annCoords = this._quadCoordsToXYWH(annCoords);
        }
        const ann = new Annotation(id, annType, annCoords, '0.9', label, color, true, {
          transcript,
          transcript_source: transcript ? 'ocr_paddle' : '',
        }, b.format || (annType === 'polygon' ? 'normalized_quad' : 'normalized_xywh'));
        this.addAnnotationClearSelection(ann);
        added += 1;
      }
      if (added === 0) {
        this.addTextBoxesStatus('result: 0 valid boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.detect_text_boxes_error'));
        return;
      }
      this.addTextBoxesStatus('result: ' + String(added) + ' box(es)');
      this.$store.dispatch('snackbars/add_success_snack', String(added) + ' text box(es) added');
    },
    getEncodedBoundingBoxes() {
      const meta = this.parseAnnotateMeta();
      if (!meta?.medication_detection_data) {
        return undefined;
      }
      try {
        const medicationDetectionData = JSON.parse(meta.medication_detection_data);
        const encodedBoundingBoxes = medicationDetectionData?.encodedBoundingBoxes;
        if (Array.isArray(encodedBoundingBoxes) && encodedBoundingBoxes.length > 0) {
          return encodedBoundingBoxes;
        }
      } catch {
        return undefined;
      }
      return undefined;
    },
    show_bbox_meta() {
      this.annotations = [];
      let encoded_bounding_boxes = this.getEncodedBoundingBoxes()
      encoded_bounding_boxes.forEach(encoded_bounding_box => {
        let bounding_box = encoded_bounding_box;
        let id = uuidV4();
        let color = [0,180,0]
        let confidence = 0.8
        let annotation = new Annotation(id, 'box', bounding_box.normalized_xywh, confidence, bounding_box.label, color);
        this.addAnnotationClearSelection(annotation)
      })
      this.show_bounding_box_meta = true;
    },
  },

  watch: {
    //
  }
};
</script>
